import Carregando from '../components/Carregando';
import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box, Typography, Card, CardContent, Grid, Chip,
  Button, Tab, Tabs, LinearProgress,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ApartmentIcon from '@mui/icons-material/Apartment';
import PhoneIphoneIcon from '@mui/icons-material/PhoneIphone';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import FolderIcon from '@mui/icons-material/Folder';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import HistoryIcon from '@mui/icons-material/History';
import api from '../services/api';
import dayjs from 'dayjs';
import 'dayjs/locale/pt-br';
dayjs.locale('pt-br');

const STATUS = {
  disponivel: { label: 'Disponível', cor: '#16A34A', pulsa: true },
  em_campo:   { label: 'Em campo',   cor: '#2563EB', pulsa: true },
  em_reuniao: { label: 'Em reunião', cor: '#D97706', pulsa: true },
  ferias:     { label: 'Férias',     cor: '#7C3AED', pulsa: false },
  afastado:   { label: 'Afastado',   cor: '#94A3B8', pulsa: false },
};

const PERFIL = {
  diretor:    { label: 'Diretor',    bg: '#d4a574', fg: '#1a1a2e' },
  gestor:     { label: 'Gestor',     bg: '#13375F', fg: '#ffffff' },
  consultor:  { label: 'Consultor',  bg: '#e94560', fg: '#ffffff' },
  financeiro: { label: 'Financeiro', bg: '#16A34A', fg: '#ffffff' },
  comercial:  { label: 'Comercial',  bg: '#2563EB', fg: '#ffffff' },
  marketing:  { label: 'Marketing',  bg: '#D97706', fg: '#ffffff' },
  rh:         { label: 'RH',         bg: '#7C3AED', fg: '#ffffff' },
};

const STATUS_OCUPACAO = {
  verde:    { label: 'No ritmo', cor: '#16A34A' },
  amarelo:  { label: 'Atenção',  cor: '#D97706' },
  vermelho: { label: 'Crítico',  cor: '#DC2626' },
};

const CATEGORIA_CONFIG = {
  consulting:  { label: 'Consulting',  cor: '#EEEDFE', textColor: '#3C3489' },
  business:    { label: 'Business',    cor: '#FDEBD3', textColor: '#8A5A00' },
  management:  { label: 'Management',  cor: '#D6EAF8', textColor: '#1A5276' },
  development: { label: 'Development', cor: '#D5F5E3', textColor: '#186A3B' },
  folga:       { label: 'Feriado / Folga', cor: '#E8E8E8', textColor: '#666666' },
};

function formatarDias(valor) {
  return Number(valor || 0).toLocaleString('pt-BR', { maximumFractionDigits: 1 });
}

function tempoDeCasa(dataAdmissao) {
  if (!dataAdmissao) return null;
  const inicio = new Date(dataAdmissao);
  const hoje = new Date();
  let meses = (hoje.getFullYear() - inicio.getFullYear()) * 12 + (hoje.getMonth() - inicio.getMonth());
  if (hoje.getDate() < inicio.getDate()) meses -= 1;
  if (meses < 0) return null;
  const anos = Math.floor(meses / 12);
  const resto = meses % 12;
  if (anos === 0) return `${resto}m de casa`;
  if (resto === 0) return `${anos}a de casa`;
  return `${anos}a ${resto}m de casa`;
}

function TabPanel({ children, value, index }) {
  return value === index ? <Box sx={{ pt: 3 }}>{children}</Box> : null;
}

export default function MembroEquipeDetalhe() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [membro, setMembro] = useState(null);
  const [ficha, setFicha] = useState(null);
  const [ocupacoes, setOcupacoes] = useState([]);
  const [lancamentos, setLancamentos] = useState([]);
  const [projetos, setProjetos] = useState([]);
  const [historicoCelulas, setHistoricoCelulas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState(0);

  useEffect(() => {
    Promise.all([
      api.get(`/membros-equipe/${id}/`),
      api.get(`/fichas-cadastrais/?membro_equipe=${id}`),
      api.get(`/ocupacao/?membro_equipe=${id}`),
      api.get(`/checkins/?membro_equipe=${id}`),
      api.get('/projetos/'),
      api.get(`/historico-celulas/?membro_equipe=${id}`),
    ]).then(([co, fc, oc, ci, pr, hc]) => {
      setMembro(co.data);
      setFicha(fc.data?.[0] || null);
      setOcupacoes(oc.data);
      setLancamentos(ci.data);
      setProjetos(pr.data);
      setHistoricoCelulas(hc.data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [id]);

  if (loading) return <Carregando />;
  if (!membro) return <Typography color="error">Membro da equipe não encontrado.</Typography>;

  const st = STATUS[membro.status] || STATUS.disponivel;
  const pf = PERFIL[membro.perfil] || { label: membro.perfil, bg: '#1a1a2e', fg: '#fff' };
  // Cargo e admissão vêm da Ficha Cadastral do RH primeiro; o campo antigo do
  // MembroEquipe só entra como reserva pra quem ainda não teve a ficha
  // preenchida (mesmo critério já usado em Meu Perfil — decisão de 23/09/2026).
  const cargo = ficha?.cargo || membro.cargo;
  const dataAdmissao = ficha?.data_admissao || membro.data_admissao;
  const dataDemissao = ficha?.data_demissao;
  const rotuloCracha = cargo || pf.label;
  const casaOuDesligado = dataDemissao
    ? `Desligado em ${dayjs(dataDemissao).format('DD/MM/YYYY')}`
    : tempoDeCasa(dataAdmissao);
  const projetosMembro = projetos.filter((p) => p.membros_equipe?.includes(parseInt(id)));

  return (
    <Box>
      <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/membros-equipe')} sx={{ color: '#1a1a2e', mb: 2 }}>
        Voltar
      </Button>

      <Box
        sx={{
          position: 'relative', borderRadius: 3, overflow: 'hidden', mb: 3,
          background: 'linear-gradient(135deg, #13375F 0%, #0d2642 100%)',
          backgroundImage: `
            repeating-linear-gradient(45deg, rgba(255,255,255,0.035) 0px, rgba(255,255,255,0.035) 1px, transparent 1px, transparent 26px),
            linear-gradient(135deg, #13375F 0%, #0d2642 100%)
          `,
          p: { xs: 2.5, sm: 3.5 },
        }}
      >
        <style>{`
          @keyframes pulseGlowDet {
            0%   { box-shadow: 0 0 0 0 var(--pulse-color); }
            70%  { box-shadow: 0 0 0 14px rgba(0,0,0,0); }
            100% { box-shadow: 0 0 0 0 rgba(0,0,0,0); }
          }
          @media (prefers-reduced-motion: reduce) { .anel-status-det { animation: none !important; } }
        `}</style>

        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2.5, alignItems: 'center' }}>
          <Box
            className="anel-status-det"
            sx={{
              width: 76, height: 76, borderRadius: '50%', p: '3px', flexShrink: 0,
              border: `2px solid ${st.cor}`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              '--pulse-color': `${st.cor}55`,
              animation: st.pulsa ? 'pulseGlowDet 2.6s ease-out infinite' : 'none',
            }}
          >
            <Box
              sx={{
                width: '100%', height: '100%', borderRadius: '50%',
                backgroundColor: '#e94560',
                backgroundImage: membro.foto_url ? `url(${membro.foto_url})` : 'none',
                backgroundSize: 'cover', backgroundPosition: 'center',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: 'white', fontSize: 26, fontWeight: 700,
              }}
            >
              {!membro.foto_url && membro.nome_completo?.charAt(0)}
            </Box>
          </Box>

          <Box sx={{ flex: 1, minWidth: 220 }}>
            <Typography variant="h5" fontWeight={700} sx={{ color: 'white', lineHeight: 1.2 }}>
              {membro.nome_completo}
            </Typography>
            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.6)', mb: 1 }}>
              {membro.email}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <Box sx={{ backgroundColor: pf.bg, color: pf.fg, px: 1.1, py: 0.3, borderRadius: 1.5, fontSize: 11.5, fontWeight: 700 }}>
                {rotuloCracha.toUpperCase()}
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'rgba(255,255,255,0.75)', fontSize: 12 }}>
                <Box sx={{ width: 7, height: 7, borderRadius: '50%', backgroundColor: st.cor }} />
                {st.label}
              </Box>
              {casaOuDesligado && (
                <Typography sx={{ color: 'rgba(255,255,255,0.4)', fontSize: 12, fontFamily: 'monospace' }}>
                  · {casaOuDesligado}
                </Typography>
              )}
            </Box>
          </Box>
        </Box>

        <Box
          sx={{
            mt: 3, pt: 2.5, borderTop: '1px solid rgba(255,255,255,0.1)',
            display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 2,
          }}
        >
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6, color: 'rgba(255,255,255,0.45)' }}>
              <ApartmentIcon sx={{ fontSize: 13 }} />
              <Typography sx={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 600 }}>Célula</Typography>
            </Box>
            <Typography sx={{ color: 'white', fontSize: 14, mt: 0.3 }}>{membro.celula?.nome || '—'}</Typography>
          </Box>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6, color: 'rgba(255,255,255,0.45)' }}>
              <PhoneIphoneIcon sx={{ fontSize: 13 }} />
              <Typography sx={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 600 }}>Telefone</Typography>
            </Box>
            <Typography sx={{ color: 'white', fontSize: 14, mt: 0.3 }}>{membro.telefone || '—'}</Typography>
          </Box>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6, color: 'rgba(255,255,255,0.45)' }}>
              <CalendarTodayIcon sx={{ fontSize: 13 }} />
              <Typography sx={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 600 }}>Admissão</Typography>
            </Box>
            <Typography sx={{ color: 'white', fontSize: 14, mt: 0.3, fontFamily: 'monospace' }}>
              {dataAdmissao ? dayjs(dataAdmissao).format('DD/MM/YYYY') : '—'}
            </Typography>
          </Box>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6, color: 'rgba(255,255,255,0.45)' }}>
              <FolderIcon sx={{ fontSize: 13 }} />
              <Typography sx={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 600 }}>Cargo</Typography>
            </Box>
            <Typography sx={{ color: 'white', fontSize: 14, mt: 0.3 }}>{cargo || '—'}</Typography>
          </Box>
        </Box>
      </Box>

      <Tabs
        value={tab} onChange={(e, v) => setTab(v)}
        variant="scrollable" scrollButtons="auto"
        sx={{ borderBottom: 1, borderColor: 'divider' }}
      >
        <Tab icon={<TrendingUpIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="Ocupação" />
        <Tab icon={<FolderIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="Projetos" />
        <Tab icon={<CalendarMonthIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="Agenda" />
        <Tab icon={<HistoryIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="Histórico de células" />
      </Tabs>

      {/* ABA OCUPAÇÃO */}
      <TabPanel value={tab} index={0}>
        {ocupacoes.length === 0 ? (
          <Card><CardContent>
            <Typography color="text.secondary" textAlign="center">Nenhum registro de ocupação encontrado.</Typography>
          </CardContent></Card>
        ) : (
          ocupacoes.map((o) => {
            const so = STATUS_OCUPACAO[o.status] || STATUS_OCUPACAO.verde;
            return (
              <Card key={o.id} sx={{ mb: 2, borderRadius: 3, overflow: 'hidden' }}>
                <Box sx={{ backgroundColor: so.cor, px: 2.5, py: 1.2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography sx={{ color: 'white', fontWeight: 700, fontSize: 14 }}>
                    {o.mes.toString().padStart(2, '0')}/{o.ano}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Typography sx={{ color: 'white', fontWeight: 700, fontFamily: 'monospace', fontSize: 15 }}>
                      {o.taxa_ocupacao}%
                    </Typography>
                    <Box sx={{ backgroundColor: 'rgba(255,255,255,0.22)', color: 'white', px: 1, py: 0.25, borderRadius: 1, fontSize: 11, fontWeight: 700 }}>
                      {so.label.toUpperCase()}
                    </Box>
                  </Box>
                </Box>
                <CardContent sx={{ p: 2.5 }}>
                  <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(110px, 1fr))', gap: 2, mb: 1.5 }}>
                    <Box>
                      <Typography sx={{ fontSize: 10.5, textTransform: 'uppercase', color: '#94a3b8', fontWeight: 700 }}>Consulting</Typography>
                      <Typography sx={{ fontFamily: 'monospace', fontWeight: 700, fontSize: 15 }}>{o.dias_consulting}d <Typography component="span" sx={{ fontSize: 11, color: '#94a3b8' }}>({o.pct_consulting}%)</Typography></Typography>
                    </Box>
                    <Box>
                      <Typography sx={{ fontSize: 10.5, textTransform: 'uppercase', color: '#94a3b8', fontWeight: 700 }}>Business</Typography>
                      <Typography sx={{ fontFamily: 'monospace', fontWeight: 700, fontSize: 15 }}>{o.dias_business}d <Typography component="span" sx={{ fontSize: 11, color: '#94a3b8' }}>({o.pct_business}%)</Typography></Typography>
                    </Box>
                    <Box>
                      <Typography sx={{ fontSize: 10.5, textTransform: 'uppercase', color: '#94a3b8', fontWeight: 700 }}>Management</Typography>
                      <Typography sx={{ fontFamily: 'monospace', fontWeight: 700, fontSize: 15 }}>{o.dias_management}d <Typography component="span" sx={{ fontSize: 11, color: '#94a3b8' }}>({o.pct_management}%)</Typography></Typography>
                    </Box>
                    <Box>
                      <Typography sx={{ fontSize: 10.5, textTransform: 'uppercase', color: '#94a3b8', fontWeight: 700 }}>Development</Typography>
                      <Typography sx={{ fontFamily: 'monospace', fontWeight: 700, fontSize: 15 }}>{o.dias_development}d <Typography component="span" sx={{ fontSize: 11, color: '#94a3b8' }}>({o.pct_development}%)</Typography></Typography>
                    </Box>
                  </Box>
                  <Typography variant="caption" color="text.secondary">
                    Dias úteis: {o.dias_uteis} · Feriado/Folga: {o.dias_folga}
                    {o.dias_sem_registro > 0 && (
                      <Typography component="span" sx={{ color: '#DC2626', fontWeight: 700 }}> · {o.dias_sem_registro} sem registro</Typography>
                    )}
                  </Typography>
                </CardContent>
              </Card>
            );
          })
        )}
      </TabPanel>

      {/* ABA PROJETOS */}
      <TabPanel value={tab} index={1}>
        {projetosMembro.length === 0 ? (
          <Card><CardContent>
            <Typography color="text.secondary" textAlign="center">Nenhum projeto encontrado para este membro da equipe.</Typography>
          </CardContent></Card>
        ) : (
          <Grid container spacing={2}>
            {projetosMembro.map((p) => {
              // Projeto de contrato novo: a carga é do CONTRATO (pool dividido entre os projetos),
              // então o cartão mostra o contrato e quanto este projeto usou. Projeto antigo: igual a antes.
              const ci = p.contrato_info || null;
              const cargaTotal = Number(ci ? ci.carga_total : p.carga_total_contratada) || 0;
              const cargaUsada = Number(ci ? ci.carga_usada : p.realizado_acumulado) || 0;
              const pct = cargaTotal > 0
                ? Math.min(Math.round((cargaUsada / cargaTotal) * 100), 100)
                : 0;
              return (
                <Grid size={{ xs: 12, sm: 6 }} key={p.id}>
                  <Card sx={{ cursor: 'pointer', borderRadius: 3 }} onClick={() => navigate(`/projetos/${p.id}`)}>
                    <CardContent>
                      <Typography variant="subtitle2" fontWeight={700} mb={0.3}>{p.nome}</Typography>
                      <Typography variant="body2" color="text.secondary" mb={ci ? 0.3 : 2}>{p.cliente_nome}</Typography>
                      {ci && (
                        <Typography variant="caption" color="text.secondary" display="block" mb={2}>
                          Contrato: {ci.nome || 'sem nome'}
                        </Typography>
                      )}
                      <Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                          <Typography variant="caption">{ci ? 'Carga do contrato' : 'Carga realizada'}</Typography>
                          <Typography variant="caption" fontWeight={600} sx={{ fontFamily: 'monospace' }}>
                            {formatarDias(cargaUsada)}d / {formatarDias(cargaTotal)}d — {pct}%
                          </Typography>
                        </Box>
                        <LinearProgress variant="determinate" value={pct} sx={{ height: 6, borderRadius: 3 }} />
                        {ci && (
                          <Typography variant="caption" color="text.secondary" display="block" mt={0.5}>
                            Este projeto usou {formatarDias(ci.este_carga_usada)}d
                            {ci.quantidade_projetos > 1 ? ` · ${ci.quantidade_projetos} projetos dividem a carga` : ''}
                          </Typography>
                        )}
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        )}
      </TabPanel>

      {/* ABA AGENDA (ex-Check-ins) */}
      <TabPanel value={tab} index={2}>
        {lancamentos.length === 0 ? (
          <Card><CardContent>
            <Typography color="text.secondary" textAlign="center">Nenhum lançamento na Agenda encontrado.</Typography>
          </CardContent></Card>
        ) : (
          <Grid container spacing={1.5}>
            {lancamentos.map((c) => {
              const cat = CATEGORIA_CONFIG[c.categoria] || CATEGORIA_CONFIG.consulting;
              const detalhe = c.categoria === 'consulting' ? (c.cliente_nome || '—') : (c.codigo_atividade_codigo || '—');
              return (
                <Grid item xs={12} sm={6} md={4} key={c.id}>
                  <Card sx={{ borderRadius: 2, borderLeft: `4px solid ${cat.textColor}` }}>
                    <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography sx={{ fontFamily: 'monospace', fontSize: 13, fontWeight: 700 }}>
                          {dayjs(c.data).format('DD/MM/YYYY')}
                        </Typography>
                        <Box sx={{ backgroundColor: cat.cor, color: cat.textColor, px: 0.9, py: 0.2, borderRadius: 1, fontSize: 10.5, fontWeight: 700 }}>
                          {cat.label.toUpperCase()}
                        </Box>
                      </Box>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.4 }}>{detalhe}</Typography>
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        )}
      </TabPanel>

      {/* ABA HISTÓRICO DE CÉLULAS */}
      <TabPanel value={tab} index={3}>
        {historicoCelulas.length === 0 ? (
          <Card><CardContent>
            <Typography color="text.secondary" textAlign="center">Nenhum histórico de células encontrado.</Typography>
          </CardContent></Card>
        ) : (
          historicoCelulas.map((h) => (
            <Card key={h.id} sx={{ mb: 1.5, borderRadius: 2 }}>
              <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 }, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                <Box>
                  <Typography fontWeight={600}>{h.celula_nome || '—'}</Typography>
                  {h.observacao && <Typography variant="caption" color="text.secondary">{h.observacao}</Typography>}
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography sx={{ fontFamily: 'monospace', fontSize: 12.5, color: '#64748b' }}>
                    {dayjs(h.data_entrada).format('DD/MM/YYYY')} → {h.data_saida ? dayjs(h.data_saida).format('DD/MM/YYYY') : 'atual'}
                  </Typography>
                  {!h.data_saida && <Chip label="Atual" color="success" size="small" />}
                </Box>
              </CardContent>
            </Card>
          ))
        )}
      </TabPanel>
    </Box>
  );
}